import React, { useEffect, useState } from 'react'
import {
  DataGrid,
  GridToolbar,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid'
import { apiClient } from 'utils/api/apiClient'
import { format } from 'date-fns'
import { Box, Tooltip } from '@mui/material'
import { DataGridStyles, dataGridBaseSx } from './DataGridStyles'

export default function AuditSummaryTable() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [userMap, setUserMap] = useState({})

  useEffect(() => {
    // load usersList from localStorage
    const saved = localStorage.getItem('usersList')
    if (saved) {
      const list = JSON.parse(saved)

      // Convert to map for O(1) lookups
      const map = list.reduce((acc, u) => {
        acc[u.id] = u
        return acc
      }, {})

      setUserMap(map)
    }
  }, [])

  const renderEllipsisTooltip = (params) => {
    const value = params.value
    if (!value) return ''

    // Convert into one item per line
    const text =
      typeof value === 'string'
        ? value
        : Array.isArray(value)
        ? value.join('\n')
        : typeof value === 'object'
        ? Object.entries(value)
            .map(([k, v]) => `${k} (${v})`)
            .join('\n')
        : String(value)

    return (
      <Tooltip
        title={
          <div
            style={{
              whiteSpace: 'pre-line', // ⬅ preserves \n breaks
              textAlign: 'left',
              overflowWrap: 'normal', // ⬅ prevent breaking long words
              wordBreak: 'keep-all', // ⬅ prevent mid-line wraps
              fontSize: '0.85rem',
              lineHeight: 1.4,
              color: '#000',
            }}
          >
            {text}
          </div>
        }
        placement="top-start"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: '#fff',
              color: '#000',
              border: '1px solid #ccc',
              borderRadius: '6px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              maxWidth: 'none', // ⬅ removes wrapping width limit
              padding: '8px 12px',
            },
          },
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'block',
            whiteSpace: 'nowrap',
          }}
        >
          {Array.isArray(value)
            ? value.join(', ')
            : typeof value === 'object'
            ? Object.entries(value)
                .map(([k, v]) => `${k} (${v})`)
                .join(', ')
            : String(value)}
        </span>
      </Tooltip>
    )
  }

  const renderFormattedField = ({ options, noneLabel = 'None Selected' }) => {
    const formatter = (params) => {
      const value = params?.value
      const label = value
        ? options.find((opt) => opt.value === value)?.label || value
        : noneLabel

      const isPlaceholder = !value || label === noneLabel

      return (
        <span
          style={isPlaceholder ? { fontStyle: 'italic', color: '#ddd' } : {}}
        >
          {label}
        </span>
      )
    }
    formatter.displayName = 'RenderFormattedFieldCell'
    return formatter
  }

  const formatDate = (value) =>
    value ? format(new Date(value), 'MM/dd/yyyy') : undefined

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      try {
        const { data } = await apiClient.get('/api/audit/summary')
        const mapped = (data.items || []).map((item) => {
          const user = userMap[item.targetUserId] || {}

          // Determine if government employee
          const isGovernmentEmployee = user?.governmentEmployee

          // Profile completion requirements
          const profileCompletionArray = isGovernmentEmployee
            ? [
                'firstName',
                'lastName',
                'email',
                'phone',
                'zipcode',
                'dateOfBirth',
                'dateOfHire',
                'dateOfPlannedRetirement',
                'agency',
                'employmentStatus',
                'baseSalary',
                'highThree',
                'netTakeHome',
                'currentLeaveBalance',
                'currentSickLeaveBalance',
                'estimatedSocialSecurity',
                'retirementSystem',
                'tsp',
                // 'tspBalance',
                // 'tspContribution',
                'tspLoans',
                'tspRoth',
                // 'tspRothBalance',
                // 'tspRothContribution',
                'familyMaritalStatus',
                'military',
                'healthInsurance',
                'healthInsurancePremium',
                'fehbCode',
                'fegli',
                'lifeInsuranceOther',
                'hasTrust',
                'hasWill',
                // 'retirementLifestyleGoal',
              ]
            : [
                'firstName',
                'lastName',
                'email',
                'phone',
                'zipcode',
                'dateOfBirth',
                'dateOfPlannedRetirement',
                'employmentStatus',
                'employerName',
                'baseSalary',
                'netTakeHome',
                'estimatedSocialSecurity',
                'familyMaritalStatus',
                'lifeInsuranceOther',
                'hasTrust',
                'hasWill',
                // 'retirementLifestyleGoal',
              ]

          // Determine missing fields
          const missingFields = profileCompletionArray.filter(
            (field) =>
              !user[field] && user[field] !== 0 && user[field] !== false
          )

          // Calculate completeness %
          const profileCompletion = Math.round(
            ((profileCompletionArray.length - missingFields.length) /
              profileCompletionArray.length) *
              100
          )

          return {
            id: item.targetUserId,
            ...item,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            createdAt: user.createdAt || item.createdAt,
            profileCompletion,
            missingFields,
          }
        })

        setRows(mapped)
      } catch (err) {
        console.error('Failed to load audit summary:', err.response || err)
      } finally {
        setLoading(false)
      }
    }
    if (Object.keys(userMap).length > 0) {
      load()
    }
  }, [userMap])

  const columns = [
    {
      field: 'targetUserId',
      headerName: 'User ID',
      flex: 1,
      renderCell: renderEllipsisTooltip,
    },
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'totalEdits', headerName: 'Total Edits', flex: 1 },
    { field: 'userEdits', headerName: 'User Edits', flex: 1 },
    { field: 'adminEdits', headerName: 'Admin Edits', flex: 1 },
    { field: 'sessions', headerName: 'Sessions', flex: 1 },
    // 🆕 Unique fields edited by admin
    {
      field: 'adminFields',
      headerName: 'Admin Edited Fields',
      flex: 1.5,
      renderCell: renderEllipsisTooltip,
    },

    // 🆕 Admin field counts
    {
      field: 'adminFieldCounts',
      headerName: 'Admin Field Counts',
      flex: 2,
      renderCell: renderEllipsisTooltip,
    },

    // 🆕 Unique fields edited by user
    {
      field: 'userFields',
      headerName: 'User Edited Fields',
      flex: 1.5,
      renderCell: renderEllipsisTooltip,
    },

    // 🆕 User field counts
    {
      field: 'userFieldCounts',
      headerName: 'User Field Counts',
      flex: 2,
      renderCell: renderEllipsisTooltip,
    },
    {
      field: 'profileCompletion',
      headerName: 'Profile Completion',
      flex: 1,
      renderCell: (params) => `${params.value}%`,
    },
    {
      field: 'missingFields',
      headerName: 'Missing Fields',
      flex: 2,
      renderCell: (params) => {
        const missing = params.value || []
        if (!missing.length) return '✓ All Complete'

        const text = missing.join('\n')

        return (
          <Tooltip
            title={
              <div
                style={{
                  whiteSpace: 'pre-line',
                  textAlign: 'left',
                  wordBreak: 'keep-all',
                  overflowWrap: 'normal',
                  color: '#000',
                }}
              >
                {text}
              </div>
            }
            placement="top-start"
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  maxWidth: 'none',
                  padding: '8px 12px',
                },
              },
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                whiteSpace: 'nowrap',
              }}
            >
              {missing.join(', ')}
            </span>
          </Tooltip>
        )
      },
    },
    {
      field: 'createdAt',
      headerName: 'User Created',
      flex: 1,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'MM/dd/yyyy') : '',
    },
    {
      field: 'updatedAt',
      headerName: 'Last Edit',
      flex: 1,
      renderCell: (params) =>
        params.value ? format(new Date(params.value), 'MM/dd/yyyy') : '',
    },
  ]

  const CustomToolbar = ({ onRefresh }) => {
    return (
      <GridToolbarContainer
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarDensitySelector />
          <GridToolbarExport csvOptions={{ fileName: 'PARCO-Audit-Export' }} />
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <GridToolbarQuickFilter debounceMs={300} />
        </Box>
      </GridToolbarContainer>
    )
  }

  return (
    <div style={{ height: 630, width: '100%' }}>
      <DataGridStyles />
      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 20, 50]}
        initialState={{
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'desc' }], // 👈 auto-sort newest first
          },
        }}
        slots={{
          toolbar: CustomToolbar, // Add the search bar
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: { fileName: 'PARCO-users-export' },
            quickFilterProps: { debounceMs: 300 },
          },
        }}
        sx={dataGridBaseSx}
      />
    </div>
  )
}
